import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { formatCurrency } from "@/lib/utils";
import { getPaymentMethodLabel } from "@/components/pdv/PDVShared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { errorTitle, TRY_AGAIN_DESCRIPTION } from "@/lib/messages";
import { extractApiErrorMessage } from "@/lib/api-error";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Banknote, Loader2, ArrowDownCircle, ArrowUpCircle, Lock, Unlock, History, TrendingUp, TrendingDown, ChevronRight } from "lucide-react";

const CASH_KEY = ["cash-current"];

// entrada = dinheiro que ENTRA na gaveta; retirada = que SAI. Mesmos valores no
// banco, na API e na tela — sem tradução.
const MOVEMENT_LABEL: Record<string, { noun: string; title: string }> = {
  entrada: { noun: "Entrada", title: "Adicionar dinheiro à gaveta" },
  retirada: { noun: "Retirada", title: "Retirar dinheiro da gaveta" },
};

function useCurrentCash() {
  return useQuery<any>({
    queryKey: CASH_KEY,
    queryFn: () => customFetch("/api/cash/current"),
    refetchInterval: 20000,
  });
}

export default function Caixa() {
  const { data, isLoading } = useCurrentCash();
  const session = data?.session ?? null;

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Banknote className="w-7 h-7 text-primary" /> Caixa
        </h1>
        <p className="text-muted-foreground mt-1">Controle da gaveta de dinheiro por turno.</p>
      </div>

      {isLoading ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : session ? (
        <OpenCash session={session} />
      ) : (
        <OpenForm />
      )}

      <ClosedHistory />
    </div>
  );
}

function OpenForm() {
  const [opening, setOpening] = useState("");
  const [operator, setOperator] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleOpen = async () => {
    setSaving(true);
    try {
      await customFetch("/api/cash/open", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          openingAmount: parseFloat(opening.replace(",", ".")) || 0,
          openedByName: operator.trim() || null,
        }),
      });
      queryClient.invalidateQueries({ queryKey: CASH_KEY });
      toast({ title: "Caixa aberto!" });
    } catch (err: any) {
      toast({ title: errorTitle("abrir o caixa"), description: extractApiErrorMessage(err, TRY_AGAIN_DESCRIPTION), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2 text-lg font-semibold"><Unlock className="w-5 h-5 text-primary" /> Abrir caixa</div>
      <p className="text-sm text-muted-foreground">
        Informe o dinheiro que já está na gaveta (fundo de troco). As vendas em dinheiro do balcão entram automaticamente enquanto o caixa estiver aberto.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Fundo de troco</Label>
          <CurrencyInput value={opening} onChange={setOpening} placeholder="R$ 0,00" />
        </div>
        <div className="space-y-1.5">
          <Label>Operador <span className="font-normal text-muted-foreground">(opcional)</span></Label>
          <Input value={operator} onChange={(e) => setOperator(e.target.value)} placeholder="Quem está no caixa" />
        </div>
      </div>
      <Button onClick={handleOpen} disabled={saving} className="w-full sm:w-auto" data-testid="cash-open">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Unlock className="w-4 h-4 mr-2" />}
        Abrir caixa
      </Button>
    </Card>
  );
}

function Row({ label, value, className = "" }: { label: React.ReactNode; value: React.ReactNode; className?: string }) {
  return (
    <div className={`flex justify-between text-sm ${className}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}

function OpenCash({ session }: { session: any }) {
  const [movementType, setMovementType] = useState<"retirada" | "entrada" | null>(null);
  const [closing, setClosing] = useState(false);
  const tally = session.tally || {};
  const byMethod: Record<string, number> = tally.byMethod || {};

  return (
    <>
      <Card className="p-6 space-y-4 border-l-2 border-l-green-500">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 text-lg font-semibold">
            <span className="inline-flex w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" /> Caixa aberto
          </div>
          <div className="text-xs text-muted-foreground text-right">
            Aberto {format(parseISO(session.openedAt), "dd/MM 'às' HH:mm", { locale: ptBR })}
            {session.openedByName ? ` · ${session.openedByName}` : ""}
          </div>
        </div>

        <div className="rounded-lg bg-muted/40 p-4 space-y-2">
          <Row label="Fundo de troco" value={formatCurrency(session.openingAmount)} />
          <Row label="Vendas em dinheiro" value={`+ ${formatCurrency(tally.cashSales || 0)}`} className="text-green-600 dark:text-green-400" />
          {session.entradas > 0 && <Row label="Entradas de dinheiro" value={`+ ${formatCurrency(session.entradas)}`} className="text-green-600 dark:text-green-400" />}
          {session.retiradas > 0 && <Row label="Retiradas de dinheiro" value={`- ${formatCurrency(session.retiradas)}`} className="text-red-600 dark:text-red-400" />}
          <div className="flex justify-between text-base font-bold pt-2 border-t border-border">
            <span>Esperado na gaveta</span>
            <span data-testid="cash-expected">{formatCurrency(session.expected)}</span>
          </div>
        </div>

        {/* Tiles com ícone em cima (padrão Entrega/Retirada do checkout): rótulo
            longo não cabe ao lado do ícone em meia largura de celular. */}
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" onClick={() => setMovementType("entrada")} className="h-auto flex-col gap-1.5 py-3 whitespace-normal" data-testid="cash-entrada">
            <ArrowUpCircle className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium leading-tight text-center">Adicionar dinheiro</span>
          </Button>
          <Button variant="outline" onClick={() => setMovementType("retirada")} className="h-auto flex-col gap-1.5 py-3 whitespace-normal" data-testid="cash-retirada">
            <ArrowDownCircle className="w-5 h-5 text-red-600" />
            <span className="text-sm font-medium leading-tight text-center">Retirar dinheiro</span>
          </Button>
        </div>

        <Button onClick={() => setClosing(true)} className="w-full" data-testid="cash-close-open">
          <Lock className="w-4 h-4 mr-2" /> Fechar caixa
        </Button>
      </Card>

      <Card className="p-6 space-y-3">
        <h3 className="font-bold">Vendas do turno</h3>
        <Row label="Pedidos (balcão)" value={tally.orderCount || 0} />
        <Row label="Total vendido" value={formatCurrency(tally.totalSales || 0)} className="font-semibold" />
        {Object.keys(byMethod).length > 0 && (
          <div className="pt-2 border-t border-border/50 space-y-1.5">
            {Object.entries(byMethod).map(([method, val]) => (
              <Row key={method} label={getPaymentMethodLabel(method)} value={formatCurrency(val)} />
            ))}
          </div>
        )}
        <p className="text-[11px] text-muted-foreground pt-1">Contando as vendas do balcão (PDV) feitas depois da abertura, às {format(parseISO(session.openedAt), "HH:mm")} — vendas de antes de abrir o caixa não entram. A conferência da gaveta é só sobre o dinheiro.</p>
      </Card>

      {(session.movements?.length ?? 0) > 0 && (
        <Card className="overflow-hidden">
          <div className="p-4 border-b border-border/50 bg-muted/10"><h3 className="font-bold">Movimentos</h3></div>
          <div className="divide-y divide-border/50">
            {session.movements.map((m: any) => (
              <div key={m.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-2 min-w-0">
                  {m.type === "entrada" ? <ArrowUpCircle className="w-4 h-4 text-green-600 shrink-0" /> : <ArrowDownCircle className="w-4 h-4 text-red-600 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{MOVEMENT_LABEL[m.type]?.noun ?? m.type}</p>
                    {m.reason && <p className="text-xs text-muted-foreground truncate">{m.reason}</p>}
                  </div>
                </div>
                <span className={`shrink-0 font-bold text-sm ${m.type === "entrada" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                  {m.type === "entrada" ? "+" : "-"}{formatCurrency(m.amount)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {movementType && <MovementDialog type={movementType} onClose={() => setMovementType(null)} />}
      {closing && <CloseDialog expected={session.expected} onClose={() => setClosing(false)} />}
    </>
  );
}

function MovementDialog({ type, onClose }: { type: "retirada" | "entrada"; onClose: () => void }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleSave = async () => {
    const val = parseFloat(amount.replace(",", "."));
    if (!Number.isFinite(val) || val <= 0) {
      toast({ title: "Informe um valor válido", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await customFetch("/api/cash/movements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, amount: val, reason: reason.trim() || null }),
      });
      queryClient.invalidateQueries({ queryKey: CASH_KEY });
      toast({ title: type === "entrada" ? "Entrada registrada!" : "Retirada registrada!" });
      onClose();
    } catch (err: any) {
      toast({ title: errorTitle("registrar o movimento"), description: extractApiErrorMessage(err, TRY_AGAIN_DESCRIPTION), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{MOVEMENT_LABEL[type].title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Valor</Label>
            <CurrencyInput value={amount} onChange={setAmount} />
          </div>
          <div className="space-y-1.5">
            <Label>Motivo <span className="font-normal text-muted-foreground">(opcional)</span></Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={type === "retirada" ? "Ex: levei pro cofre" : "Ex: coloquei mais troco"} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="cash-movement-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloseDialog({ expected, onClose }: { expected: number; onClose: () => void }) {
  const [counted, setCounted] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const countedNum = parseFloat(counted.replace(",", ".")) || 0;
  const diff = counted === "" ? null : Math.round((countedNum - expected) * 100) / 100;

  const handleClose = async () => {
    setSaving(true);
    try {
      await customFetch("/api/cash/close", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ countedAmount: countedNum, notes: notes.trim() || null }),
      });
      queryClient.invalidateQueries({ queryKey: CASH_KEY });
      queryClient.invalidateQueries({ queryKey: ["cash-history"] });
      toast({ title: "Caixa fechado!" });
      onClose();
    } catch (err: any) {
      toast({ title: errorTitle("fechar o caixa"), description: extractApiErrorMessage(err, TRY_AGAIN_DESCRIPTION), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Fechar caixa</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg bg-muted/40 p-3">
            <Row label="Esperado na gaveta" value={formatCurrency(expected)} className="font-semibold" />
          </div>
          <div className="space-y-1.5">
            <Label>Dinheiro contado</Label>
            <CurrencyInput value={counted} onChange={setCounted} />
          </div>
          {diff !== null && (
            <div className={`flex items-center justify-between rounded-lg p-3 text-sm font-bold ${Math.abs(diff) < 0.005 ? "bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-300" : diff > 0 ? "bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300" : "bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300"}`}>
              <span className="flex items-center gap-1.5">
                {Math.abs(diff) < 0.005 ? "Bateu certinho" : diff > 0 ? <><TrendingUp className="w-4 h-4" /> Sobra</> : <><TrendingDown className="w-4 h-4" /> Falta</>}
              </span>
              <span>{diff === 0 ? "R$ 0,00" : `${diff > 0 ? "+" : "-"}${formatCurrency(Math.abs(diff))}`}</span>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Observações <span className="font-normal text-muted-foreground">(opcional)</span></Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: falta por troco a mais" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleClose} disabled={saving || counted === ""} data-testid="cash-close-confirm">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />} Fechar caixa
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClosedHistory() {
  const { data } = useQuery<any>({
    queryKey: ["cash-history"],
    queryFn: () => customFetch("/api/cash/sessions"),
  });
  const [detailId, setDetailId] = useState<string | null>(null);
  const sessions = data?.sessions ?? [];
  if (sessions.length === 0) return null;
  return (
    <Card className="overflow-hidden">
      <div className="p-4 border-b border-border/50 bg-muted/10">
        <h3 className="font-bold flex items-center gap-2"><History className="w-4 h-4 text-muted-foreground" /> Caixas anteriores</h3>
      </div>
      <div className="divide-y divide-border/50">
        {sessions.map((s: any) => {
          const diff = s.differenceAmount != null ? parseFloat(s.differenceAmount) : 0;
          return (
            <button
              type="button"
              key={s.id}
              onClick={() => setDetailId(s.id)}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors cursor-pointer"
              data-testid="cash-history-row"
            >
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {s.closedAt ? format(parseISO(s.closedAt), "dd/MM/yyyy", { locale: ptBR }) : "—"}
                  {s.openedByName ? ` · ${s.openedByName}` : ""}
                </p>
                <p className="text-xs text-muted-foreground">
                  Contado {formatCurrency(s.countedAmount || 0)} · esperado {formatCurrency(s.expectedAmount || 0)}
                </p>
              </div>
              <span className="flex items-center gap-1.5 shrink-0">
                <span className={`text-xs font-bold px-2 py-1 rounded ${Math.abs(diff) < 0.005 ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" : diff > 0 ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" : "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"}`}>
                  {Math.abs(diff) < 0.005 ? "OK" : `${diff > 0 ? "+" : "-"}${formatCurrency(Math.abs(diff))}`}
                </span>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </span>
            </button>
          );
        })}
      </div>
      {detailId && <SessionDetailDialog id={detailId} onClose={() => setDetailId(null)} />}
    </Card>
  );
}

function SessionDetailDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { data, isLoading } = useQuery<any>({
    queryKey: ["cash-session", id],
    queryFn: () => customFetch(`/api/cash/sessions/${id}`),
  });
  const s = data?.session ?? null;
  const tally = s?.tally || {};
  const byMethod: Record<string, number> = tally.byMethod || {};
  const diff = s?.differenceAmount != null ? parseFloat(s.differenceAmount) : 0;

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalhes do caixa</DialogTitle>
        </DialogHeader>
        {isLoading || !s ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Aberto {format(parseISO(s.openedAt), "dd/MM 'às' HH:mm", { locale: ptBR })}
              {s.closedAt ? ` · Fechado ${format(parseISO(s.closedAt), "dd/MM 'às' HH:mm", { locale: ptBR })}` : ""}
              {s.openedByName ? ` · ${s.openedByName}` : ""}
            </p>

            <div className="rounded-lg bg-muted/40 p-4 space-y-2">
              <Row label="Fundo de troco" value={formatCurrency(s.openingAmount)} />
              <Row label="Vendas em dinheiro" value={`+ ${formatCurrency(tally.cashSales || 0)}`} className="text-green-600 dark:text-green-400" />
              {s.entradas > 0 && <Row label="Entradas de dinheiro" value={`+ ${formatCurrency(s.entradas)}`} className="text-green-600 dark:text-green-400" />}
              {s.retiradas > 0 && <Row label="Retiradas de dinheiro" value={`- ${formatCurrency(s.retiradas)}`} className="text-red-600 dark:text-red-400" />}
              <div className="pt-2 border-t border-border space-y-2">
                <Row label="Esperado na gaveta" value={formatCurrency(s.expected)} className="font-semibold" />
                <Row label="Contado no fechamento" value={formatCurrency(s.countedAmount || 0)} className="font-semibold" />
                <div className={`flex justify-between text-base font-bold ${Math.abs(diff) < 0.005 ? "text-green-700 dark:text-green-300" : diff > 0 ? "text-blue-700 dark:text-blue-300" : "text-red-700 dark:text-red-300"}`}>
                  <span className="flex items-center gap-1.5">
                    {Math.abs(diff) < 0.005 ? "Bateu certinho" : diff > 0 ? <><TrendingUp className="w-4 h-4" /> Sobra</> : <><TrendingDown className="w-4 h-4" /> Falta</>}
                  </span>
                  <span>{Math.abs(diff) < 0.005 ? "R$ 0,00" : `${diff > 0 ? "+" : "-"}${formatCurrency(Math.abs(diff))}`}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-bold text-sm">Vendas do turno</h4>
              <Row label="Pedidos (balcão)" value={tally.orderCount || 0} />
              <Row label="Total vendido" value={formatCurrency(tally.totalSales || 0)} className="font-semibold" />
              {Object.keys(byMethod).length > 0 && (
                <div className="pt-2 border-t border-border/50 space-y-1.5">
                  {Object.entries(byMethod).map(([method, val]) => (
                    <Row key={method} label={getPaymentMethodLabel(method)} value={formatCurrency(val)} />
                  ))}
                </div>
              )}
            </div>

            {(s.movements?.length ?? 0) > 0 && (
              <div className="space-y-2">
                <h4 className="font-bold text-sm">Movimentos</h4>
                <div className="rounded-lg border divide-y divide-border/50">
                  {s.movements.map((m: any) => (
                    <div key={m.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                      <div className="flex items-center gap-2 min-w-0">
                        {m.type === "entrada" ? <ArrowUpCircle className="w-4 h-4 text-green-600 shrink-0" /> : <ArrowDownCircle className="w-4 h-4 text-red-600 shrink-0" />}
                        <div className="min-w-0">
                          <p className="text-sm font-medium">{MOVEMENT_LABEL[m.type]?.noun ?? m.type}</p>
                          {m.reason && <p className="text-xs text-muted-foreground truncate">{m.reason}</p>}
                        </div>
                      </div>
                      <span className={`shrink-0 font-bold text-sm ${m.type === "entrada" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                        {m.type === "entrada" ? "+" : "-"}{formatCurrency(m.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {s.notes && (
              <p className="text-sm"><span className="font-medium">Observações:</span> <span className="text-muted-foreground">{s.notes}</span></p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
